import { SyntheticEvent, useEffect, useState } from 'react';
import { Nav, NavItem, Row, TabContainer } from 'react-bootstrap';
import { useFieldArray, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Button from 'components/button/Button';
import { VerticalTabContainer } from 'components/tab/Tab.styles';
import { RoomType } from 'models/PropertyRooms';
import { classNames } from 'utils/classNameUtils';
import { RoomsAndBedTypesFormValues } from './RoomsAndBedTypesModal.schema';
import {
  RoomsDescriptionContainer,
  RoomsAndBedTypesColStyled,
  RoomsTabsContainer,
  RoomsTabsDivider,
} from './RoomsAndBedTypesFormElements.styles';
import RoomsAndBedTypesFormRoomTypeField from './RoomsAndBedTypesFormRoomTypeField';
import RoomsAndBedTypesFormBedTypes from './RoomsAndBedTypesFormBedTypes';
import RoomsAndBedTypesFormPrivateBathroomField from './RoomsAndBedTypesFormPrivateBathroomField';
import RoomsAndBedTypesFormSharedRoomField from './RoomsAndBedTypesFormSharedRoomField';
import RoomsAndBedTypesFormDeleteRoomButton from './RoomsAndBedTypesFormDeleteRoomButton';

const RoomsAndBedTypesFormElements = () => {
  const {
    fields: rooms,
    append,
    remove,
    update,
  } = useFieldArray<RoomsAndBedTypesFormValues, 'rooms'>({
    name: 'rooms',
  });
  const { errors } = useFormState();
  const [selectedRoomIndex, setSelectedRoomIndex] = useState<number>(0);
  const { t } = useTranslation();

  useEffect(() => {
    if (Object.keys(errors?.rooms || {}).length) {
      // @ts-expect-error TS2769 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      const currentRoomHasNoError = !Object.keys(errors.rooms).some(
        (roomIndex) => Number(roomIndex) === selectedRoomIndex,
      );

      if (currentRoomHasNoError) {
        // @ts-expect-error TS2769 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
        const firstTabWithError = Number(Object.keys(errors.rooms)[0]);
        setSelectedRoomIndex(firstTabWithError);
      }
    }
  }, [errors]);

  const onSelect = (event: SyntheticEvent<TabContainer, Event>) => {
    const tabIndex = event as unknown as number;
    setSelectedRoomIndex(tabIndex);
  };

  const addRoomHandler = () => {
    const roomIndex = rooms.length;
    append({
      beds: [],
      bedTypes: '',
      hasPrivateBathroom: false,
      sharedRoom: false,
      roomType: RoomType.BEDROOM,
      uid: null,
    });
    setSelectedRoomIndex(roomIndex);
  };

  const deleteRoomHandler = (roomIndex: number) => {
    remove(roomIndex);

    if (roomIndex > 0) {
      setSelectedRoomIndex(roomIndex - 1);
    }
  };

  const updateRoomType = (roomIndex: number, roomType: RoomType) => {
    const room = rooms[roomIndex];
    update(roomIndex, { ...room, roomType });
  };

  return (
    <>
      <RoomsDescriptionContainer>
        <h4 data-testid="rooms-and-bed-types-title">
          {t(
            'pageProperty.mainSettings.capacityDetails.setRoomsAndBedTypesModal.roomsAndBedTypes',
          )}
        </h4>
        <Button
          $colorScheme="green"
          data-testid="add-room-button"
          onClick={addRoomHandler}
        >
          {t(
            'pageProperty.mainSettings.capacityDetails.setRoomsAndBedTypesModal.addRoomButtonLabel',
          )}
        </Button>
      </RoomsDescriptionContainer>
      {!rooms?.length && (
        <div data-testid="no-rooms-message">
          {t(
            'pageProperty.mainSettings.capacityDetails.setRoomsAndBedTypesModal.noRoomsMessage',
          )}
        </div>
      )}
      {!!rooms?.length && (
        <>
          <RoomsTabsDivider />
          <VerticalTabContainer
            activeKey={selectedRoomIndex}
            onSelect={onSelect}
          >
            <Row>
              <RoomsAndBedTypesColStyled sm={4}>
                <Nav stacked>
                  {rooms.map((room, index) => {
                    // dynamically calculate the room number for this type
                    const roomNumber = rooms
                      .slice(0, index + 1)
                      .filter((r) => r.roomType === room.roomType).length;

                    return (
                      <NavItem
                        data-testid={`room-tab-${index}`}
                        eventKey={index}
                        key={`${room.roomType}-${roomNumber}`}
                      >
                        {`${t(
                          `componentProperty.rooms.roomType.${room.roomType}`,
                        )} ${roomNumber}`}
                      </NavItem>
                    );
                  })}
                </Nav>
              </RoomsAndBedTypesColStyled>
              <RoomsAndBedTypesColStyled sm={8}>
                {rooms.map(({ roomType, uid, id }, index) => {
                  const isBedroomOrLivingRoom =
                    roomType === RoomType.BEDROOM ||
                    roomType === RoomType.LIVING_ROOM;

                  return (
                    <RoomsTabsContainer
                      className={classNames({
                        'd-none': index !== selectedRoomIndex,
                      })}
                      data-testid={`room-details-${index}`}
                      key={id}
                    >
                      <RoomsAndBedTypesFormRoomTypeField
                        roomIndex={index}
                        update={updateRoomType}
                        isEditing={!!uid}
                      />
                      {isBedroomOrLivingRoom && (
                        <>
                          <RoomsAndBedTypesFormPrivateBathroomField
                            roomIndex={index}
                          />
                          <RoomsAndBedTypesFormSharedRoomField
                            roomIndex={index}
                          />
                          <RoomsAndBedTypesFormBedTypes roomIndex={index} />
                        </>
                      )}
                      <RoomsAndBedTypesFormDeleteRoomButton
                        deleteRoomHandler={() => deleteRoomHandler(index)}
                      />
                    </RoomsTabsContainer>
                  );
                })}
              </RoomsAndBedTypesColStyled>
            </Row>
          </VerticalTabContainer>
        </>
      )}
    </>
  );
};

export default RoomsAndBedTypesFormElements;
